import type { AnalysisGroup, GroupExecutionList, GroupMember, GroupRunResult } from '../model.js';
import { DEFAULT_ENGINE_CATALOGUE, engineLabel } from './engine-rules.js';
import { executionSettled, executionState } from './group-executions.js';
import { groupInFlight } from './hub-digest.js';

/**
 * What a group launch answered, in words (GAP-11).
 *
 * The toasts used to say that every skipped member «tiene una ejecución fijada». Upstream never
 * skips a pinned member — pinning decides which run a member is evaluated by, not whether it runs —
 * so the sentence was wrong every time it appeared. These are the reasons upstream actually gives.
 */

const SKIP_REASONS: Record<string, { one: string; many: string }> = {
  already_active: {
    one: 'ya tiene un análisis en marcha en el grupo',
    many: 'ya tienen un análisis en marcha en el grupo',
  },
  engines_not_selectable: {
    one: 'no admite ninguno de los motores elegidos',
    many: 'no admiten ninguno de los motores elegidos',
  },
  source_not_found: { one: 'ya no existe', many: 'ya no existen' },
  launch_failed: {
    one: 'Virtual Profiler no lo pudo lanzar',
    many: 'Virtual Profiler no los pudo lanzar',
  },
  not_a_member: {
    one: 'no es miembro del grupo',
    many: 'no son miembros del grupo',
  },
  // A launch fed by the labels read the gestures labelling until 2026-10-29: executions stored
  // before that still carry these two. Since then it skips by the alerts (`alert_labels_missing`).
  gesture_labels_missing: {
    one: 'no tiene gestos etiquetados',
    many: 'no tienen gestos etiquetados',
  },
  gesture_labelling_incomplete: {
    one: 'no tiene el etiquetado completo',
    many: 'no tienen el etiquetado completo',
  },
  // A control run leaves out a pending video by its first missing piece; semantics and coherence
  // score each video against its truth or lie. «Sin verdad o mentira», as the control cards say it.
  truth_label_missing: {
    one: 'sin verdad o mentira',
    many: 'sin verdad o mentira',
  },
  // Alertas gestuales skips a video by its alerts labelling, and so does Semántica, fed them: one
  // not marked complete, or complete without alerts.
  alert_labels_missing: {
    one: 'sin alertas etiquetadas o sin el etiquetado de alertas completo',
    many: 'sin alertas etiquetadas o sin el etiquetado de alertas completo',
  },
  // Coherence reads the video's own transcription: a video whose job is not done waits for it.
  transcription_not_ready: {
    one: 'sin transcripción lista',
    many: 'sin transcripción lista',
  },
};

/**
 * One reason, for one member or for several.
 *
 * An unknown reason is shown as upstream spelled it rather than dropped: a reason added after this
 * build is still more useful to read verbatim than to be told nothing.
 */
export function skipReasonLabel(reason: string, count = 1): string {
  const known = SKIP_REASONS[reason];

  if (!known) return `motivo «${reason}»`;

  return count === 1 ? known.one : known.many;
}

/**
 * The sentence after a group launch.
 *
 * Skips are counted by reason, never lumped: «2 omitidos» with no why sends somebody through the
 * member list one by one. A launch that waits its turn has launched nothing yet, so it says where
 * it waits instead of «No había nada que lanzar».
 */
export function groupRunMessage(
  result: Pick<GroupRunResult, 'accepted' | 'skipped' | 'queued' | 'queuePosition'>,
): string {
  if (result.queued) {
    const place = result.queuePosition === null ? '' : ` (#${result.queuePosition})`;

    return `En cola${place}: empezará cuando termine la ejecución en marcha`;
  }

  const launched = result.accepted.length;

  if (result.skipped.length === 0) {
    return launched === 0 ? 'No había nada que lanzar' : `${launched} análisis lanzados`;
  }

  const byReason = new Map<string, number>();

  for (const skip of result.skipped) {
    byReason.set(skip.reason, (byReason.get(skip.reason) ?? 0) + 1);
  }

  const reasons = [...byReason.entries()]
    .map(([reason, count]) => `${count} ${skipReasonLabel(reason, count)}`)
    .join('; ');

  return `${launched} lanzados y ${result.skipped.length} omitidos: ${reasons}`;
}

/** What the launch dialog adds when the group is busy and the execution will wait its turn. */
export const QUEUED_LAUNCH_NOTE =
  'Se pondrá en cola: empezará cuando termine la ejecución en marcha.';

/**
 * Whether a launch now would wait its turn behind the group's execution in flight.
 *
 * Virtual Profiler decides it (product owner, 2026-09-26): the group is busy while any run of it
 * moves — its latest execution, a cancelled one still stopping, a retry, a replay — or an execution
 * already waits. This only says it before the launch, from what the screen can see: the list, and
 * the queue or the group's own executions, which are polled apart and may see it first.
 *
 * A paused execution has not finished, and still never holds the group (product owner, 2026-09-29):
 * only the runs of it that had started before the pause do, while they finish.
 */
export function launchWillQueue(
  group: Pick<AnalysisGroup, 'runsByStatus' | 'executionsWaiting'>,
  seen: {
    activity?: { running: number; waiting: number } | null;
    executions?: Pick<GroupExecutionList, 'items' | 'waiting'> | null;
  } = {},
): boolean {
  const queue = (seen.activity?.running ?? 0) + (seen.activity?.waiting ?? 0);
  const executions = seen.executions;

  return (
    groupInFlight(group) > 0 ||
    group.executionsWaiting > 0 ||
    queue > 0 ||
    (executions?.waiting.length ?? 0) > 0 ||
    (executions?.items ?? []).some(
      (batch) =>
        !batch.complete && (executionState(batch) !== 'paused' || !executionSettled(batch)),
    )
  );
}

/**
 * What is asked before launching a group, with the engines chosen in the launch dialog.
 *
 * Asked at all because it queues one analysis per member. Two short sentences (product owner,
 * 2026-09-25): what is analysed, with which engines, and that it makes a new execution — never what
 * it costs. The configuration, its version and the threshold are the dialog's own fields, right
 * below: the sentence does not repeat them.
 */
export function groupRunConfirmation(
  group: Pick<AnalysisGroup, 'name' | 'members'>,
  engines: readonly string[],
): {
  title: string;
  description: string;
  actionLabel: string;
} {
  const names =
    engines.length > 0
      ? engines.map((name) => engineLabel(DEFAULT_ENGINE_CATALOGUE, name)).join(' y ')
      : 'los motores elegidos';
  const analysed =
    group.members === 1
      ? 'Se analizará su único vídeo'
      : `Se analizarán sus ${group.members} vídeos`;
  return {
    title: `Nueva ejecución de «${group.name}»`,
    description: `${analysed} con ${names}. Se genera una nueva ejecución.`,
    actionLabel: 'Lanzar',
  };
}

/**
 * What a group run's estimate is asked over, or null when it has no video to estimate.
 *
 * The videos among its members, with the engines chosen for the launch: that is what the run
 * launches. The estimate route only knows videos, so an interview member is left out rather than
 * sent to fail.
 */
export function groupEstimateRequest(
  members: readonly Pick<GroupMember, 'sourceId' | 'sourceType'>[],
  engines: readonly string[],
): { videoIds: number[]; engines: string[] } | null {
  const videoIds = members
    .filter((member) => member.sourceType === 'manual_analysis')
    .map((member) => member.sourceId);

  return videoIds.length > 0 ? { videoIds, engines: [...engines] } : null;
}
